import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { BillingAddressFormFields, BillingAddressFormObj, ConnectedForms, UNITED_STATES } from './billing-address.config';
import { Observable, Subscription } from 'rxjs';
import { ValidationService } from '../../service/validation/validation.service';
import { MenuOption } from '../menu-input/menu-input.config';
import { Countries } from '../../constants/countries';

@Component({
  selector: 'dn-billing-address',
  templateUrl: './billing-address.component.html',
  styleUrl: './billing-address.component.scss',
  standalone: false,
})
export class BillingAddressComponent implements OnInit, OnDestroy {
  @Input() billingForm: FormGroup;
  @Input() checkForm: Observable<void>;
  @Input() emailField: boolean;
  @Input() initValues: BillingAddressFormObj;
  @Output() newError: EventEmitter<any> = new EventEmitter<any>();
  connectForms = ConnectedForms;
  countries: Array<MenuOption> = Countries;
  states: Array<MenuOption> = [];
  errors: string;
  checkError: Subscription;
  formValues = BillingAddressFormFields;

  constructor(private readonly validationService: ValidationService) {}

  ngOnInit(): void {
    this.billingForm.addControl(BillingAddressFormFields.Street, new FormControl('', Validators.required));
    this.billingForm.addControl(BillingAddressFormFields.Country, new FormControl('', Validators.required));
    this.billingForm.addControl(BillingAddressFormFields.State, new FormControl('', Validators.required));
    this.billingForm.addControl(BillingAddressFormFields.City, new FormControl('', Validators.required));
    this.billingForm.addControl(BillingAddressFormFields.ZipCode, new FormControl(''));

    if (this.emailField) {
      this.billingForm.addControl(BillingAddressFormFields.Email, new FormControl('', [Validators.required, this.validationService.validateEmail]));
    }

    if (this.initValues) {
      this.billingForm.patchValue({
        [BillingAddressFormFields.Street]: this.initValues.street,
        [BillingAddressFormFields.Country]: this.initValues.country,
        [BillingAddressFormFields.State]: this.initValues.state,
        [BillingAddressFormFields.City]: this.initValues.city,
        [BillingAddressFormFields.ZipCode]: this.initValues.zipCode,
      });

      if (this.emailField) {
        this.billingForm.patchValue({
          [BillingAddressFormFields.Email]: this.initValues.email,
        });
      }
    }

    if (this.billingForm.get(BillingAddressFormFields.Country).value === UNITED_STATES) {
      const unitedStatesOption = this.countries.find((x) => x.value === UNITED_STATES);
      this.setStates(unitedStatesOption);
      this.updateFormIfCountryUsaIsSelected();
    }

    setTimeout(() => {
      this.checkError = this.checkForm.subscribe(() => this.showErrors());
    }, 0);
  }

  onCountrySelect(option: MenuOption): void {
    if (this.billingForm.get(BillingAddressFormFields.State).value && option.value === UNITED_STATES) {
      return;
    }

    this.billingForm.get(BillingAddressFormFields.State).setValue('');

    this.setStates(option);

    if (this.billingForm.get(BillingAddressFormFields.Country).value === UNITED_STATES) {
      this.updateFormIfCountryUsaIsSelected();
    } else {
      this.updateFormIfCountryIsNotUsa();
    }
  }

  setStates(option: MenuOption): void {
    if (option.states && option.states.length && option.value === UNITED_STATES) {
      this.states = option.states;
    } else {
      this.states = [];
    }
  }

  showErrors(): void {
    for (const formCtrl in this.billingForm.controls) {
      // eslint-disable-next-line no-prototype-builtins
      if (!this.billingForm.controls.hasOwnProperty(formCtrl)) {
        continue;
      }
      const field: AbstractControl = this.billingForm.controls[formCtrl];
      if (field.invalid) {
        field.markAsDirty();
        field.markAsTouched();
      }
    }
  }

  ngOnDestroy(): void {
    this.checkError.unsubscribe();
  }

  updateFormIfCountryUsaIsSelected(): void {
    this.billingForm.get(BillingAddressFormFields.State).setValidators([Validators.required]);

    if (!this.billingForm.get(BillingAddressFormFields.State).value) {
      this.billingForm.get(BillingAddressFormFields.State).setErrors({ required: true });
      this.billingForm.get(BillingAddressFormFields.State).markAsDirty();
      this.billingForm.get(BillingAddressFormFields.State).markAsTouched();
    }
    this.billingForm.get(BillingAddressFormFields.State).updateValueAndValidity();
  }

  updateFormIfCountryIsNotUsa(): void {
    this.billingForm.get(BillingAddressFormFields.State).clearValidators();
    this.billingForm.get(BillingAddressFormFields.State).updateValueAndValidity();
  }
}
